import * as React from 'react'
import { cn } from '../lib/cn'

/**
 * Desk mention markup and Riley's highlight markers, rendered (issue #113). Mirrors apps/api/app/mentions/markup.py:
 * Desk stores a mention as `zsu[@user:<ZUID>]zsu` (team: `zsu[@team:<id>]zsu`); Riley keeps a customer's inline
 * highlight as `[[highlighted by sender: ...]]`. Neither marker is ever shown raw.
 */
export type MarkupPart =
  | { kind: 'text'; text: string }
  | { kind: 'mention'; zuid: string }
  | { kind: 'team'; id: string }
  | { kind: 'highlight'; text: string }

export const UNKNOWN_TEAMMATE = 'unknown teammate'

const MARKUP = /zsu\[@(user|team):(\d{1,20})(?::[^\]]{0,200})?\]zsu|\[\[highlighted by sender:\s*([\s\S]*?)\]\]/gi

export function splitMarkup(text: string): MarkupPart[] {
  const out: MarkupPart[] = []
  let last = 0
  for (const m of text.matchAll(MARKUP)) {
    const at = m.index ?? 0
    if (at > last) out.push({ kind: 'text', text: text.slice(last, at) })
    if (m[1] !== undefined) {
      // The mention prefix is lower-case in Desk; the /i flag is for the highlight marker only.
      if (!m[0].startsWith('zsu[@')) out.push({ kind: 'text', text: m[0] })
      else if (m[1].toLowerCase() === 'team') out.push({ kind: 'team', id: m[2] ?? '' })
      else out.push({ kind: 'mention', zuid: m[2] ?? '' })
    } else {
      out.push({ kind: 'highlight', text: (m[3] ?? '').trim() })
    }
    last = at + m[0].length
  }
  if (last < text.length) out.push({ kind: 'text', text: text.slice(last) })
  return out
}

export function mentionName(zuid: string, mentions?: Record<string, string>): string {
  const name = mentions?.[zuid]?.trim()
  return name || UNKNOWN_TEAMMATE
}

/** The plain-text form for surfaces that cannot hold elements (titles, one-line snippets): `@Name`, highlight words. */
export function plainMarkup(text: string | null | undefined, mentions?: Record<string, string>): string {
  if (!text) return ''
  return splitMarkup(text)
    .map((p) =>
      p.kind === 'text' ? p.text : p.kind === 'highlight' ? p.text : p.kind === 'team' ? '@team' : `@${mentionName(p.zuid, mentions)}`,
    )
    .join('')
}

export function MentionChip({ name, unknown, className }: { name: string; unknown?: boolean; className?: string }) {
  return (
    <span
      data-slot="mention"
      data-unknown={unknown ? 'true' : undefined}
      className={cn(
        'inline-flex items-baseline rounded-sm bg-primary-soft px-1 font-medium text-primary',
        unknown && 'bg-muted text-muted-foreground',
        className,
      )}
    >
      @{name}
    </span>
  )
}

/** Inline content (no wrapping element): drop it inside a <p> or any text container. */
export function MarkupText({ text, mentions }: { text: string; mentions?: Record<string, string> }) {
  const parts = React.useMemo(() => splitMarkup(text), [text])
  return (
    <>
      {parts.map((p, i) => {
        const key = i
        if (p.kind === 'text') return <React.Fragment key={key}>{p.text}</React.Fragment>
        if (p.kind === 'highlight')
          return (
            <mark key={key} className="rounded-xs bg-warning-soft px-0.5 text-foreground">
              {p.text}
            </mark>
          )
        if (p.kind === 'team') return <MentionChip key={key} name="team" />
        const name = mentionName(p.zuid, mentions)
        return <MentionChip key={key} name={name} unknown={name === UNKNOWN_TEAMMATE} />
      })}
    </>
  )
}
